import React from 'react';

import styled from '@emotion/styled';
import type { ButtonProps } from '@rtkwlf/fenrir-react';
import {
  buttonStyle,
  buttonVariants,
  ExternalLink,
  HStack,
} from '@rtkwlf/fenrir-react';
import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { joinTruthies } from '@packages/utils/join';

const StyledLink = styled(Link)<{ 'data-padding'?: string | number }>(
  ({ 'data-padding': padding }) => ({
    fontWeight: 'bold',
    padding,
    textDecoration: 'none',
  }),
);

export interface LinkButtonProps extends LinkProps {
  /** Fenrir's button's variants or inline, to have it render as a simple `<a/>` */
  variant?: ButtonProps['variant'] | 'inline';
  appearance?: ButtonProps['appearance'];
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  external?: boolean;
  padding?: string | number;
}

const variantClassName = {
  link: buttonVariants.link,
  primary: buttonVariants.primary,
  secondary: buttonVariants.secondary,
  tertiary: buttonVariants.tertiary,
};

export const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      variant = 'primary',
      appearance = 'default',
      className,
      children,
      iconLeft,
      iconRight,
      external,
      padding,
      ...rest
    },
    ref,
  ) => {
    const linkProps = {
      ref,
      rel: external ? 'noopener noreferrer' : undefined,
      target: external ? '_blank' : undefined,
    };

    if (variant === 'inline') {
      return (
        <Link {...linkProps} {...rest}>
          {children}
          {external ? <ExternalLink paddingLeft="xsmall" /> : null}
        </Link>
      );
    }

    const buttonClassName = variantClassName[variant][appearance];

    const linkClassName = joinTruthies(
      [buttonStyle, buttonClassName, className],
      ' ',
    );

    return (
      <StyledLink
        {...linkProps}
        className={linkClassName}
        data-padding={padding}
        {...rest}
      >
        <HStack gap="small">
          {iconLeft}
          {children}
          {external && iconRight === undefined ? <ExternalLink /> : iconRight}
        </HStack>
      </StyledLink>
    );
  },
);
